import React from "react";
import { Box, Divider, Typography, SxProps, Theme } from "@mui/material";
import CustomTruncateText from "../TruncateText/CustomTruncateText";

export interface CustomSectionTitleProps {
  title: string;
  addBorder?: boolean;
  sx?: SxProps<Theme>;
  size?: "small" | "normal" | "large";
  color?: string;
  fontFamily?: string;
  fontWeight?: number | string;
  /**
   * 指定すると左側の縦線の代わりにアイコンを表示する
   */
  icon?: React.ReactNode;
  /**
   * true にすると CustomTruncateText を通さず title をそのまま表示する。
   * width:auto で flex 内に置く時など、自己参照 truncation を回避したい場合に使う。
   */
  noTruncate?: boolean;
  /**
   * CustomTruncateText の幅計算基準にする外側コンテナ ref。
   * Stack/flex 内で width:auto の親 (= 自分自身) を基準にすると自己参照で誤省略するため、
   * 親 Stack 等の ref を渡すと、その内側幅で省略判定する。
   */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** containerRef を使う時、内側幅から差し引くオフセット (隣接 chip 等の幅) */
  containerOffset?: number;
}

const CustomSectionTitle: React.FC<CustomSectionTitleProps> = ({
  title,
  addBorder = true,
  sx,
  size = "normal",
  color,
  fontFamily,
  fontWeight = "bold",
  icon,
  noTruncate = false,
  containerRef,
  containerOffset,
}) => {
  const hasIcon = Boolean(icon);

  const sizeStyles =
    size === "large"
      ? {
          fontSize: { xs: "20px", sm: "20px", md: "20px" },
          lineHeight: { xs: "24px", sm: "26px", md: "28px" },
        }
      : size === "small"
        ? {
            fontSize: "14px",
            lineHeight: "18px",
          }
        : {
            fontSize: "16px",
            lineHeight: "20px",
          };

  const marginBottomPx = "6px";

  return (
    <>
      {hasIcon ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            mb: marginBottomPx,
            ...sx,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="subtitle1"
            component="h1"
            fontWeight={fontWeight}
            color={color}
            fontFamily={fontFamily}
            sx={{
              flex: 1,
              minWidth: 0,
              ...sizeStyles,
            }}
          >
            {noTruncate ? (
            title
          ) : (
            <CustomTruncateText
              text={title}
              containerRef={containerRef}
              containerOffset={containerOffset}
            />
          )}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="subtitle1"
          component="h1"
          fontWeight={fontWeight}
          fontSize={
            size === "large" ? undefined : size === "small" ? "14px" : "16px"
          }
          lineHeight={
            size === "large" ? undefined : size === "small" ? "18px" : "20px"
          }
          position={"relative"}
          color={color}
          fontFamily={fontFamily}
          sx={{
            width: "100%",
            ...sizeStyles,
            borderLeft: { xs: "6px solid #0B9DBD", sm: "7px solid #0B9DBD" },
            mb: marginBottomPx,
            ...sx,
          }}
          pl={1.25}
        >
          {noTruncate ? (
            title
          ) : (
            <CustomTruncateText
              text={title}
              containerRef={containerRef}
              containerOffset={containerOffset}
            />
          )}
        </Typography>
      )}
      {addBorder && <Divider sx={{ borderColor: "#D9D9D9" }} />}
    </>
  );
};

export default CustomSectionTitle;
